import { Dispatch } from 'redux';
import MyLeadsController from '../controllers/MyLeadsController';
import commonHandler from '../errorHandlers/common-handler';
import { SearchAttributes } from '../interfaces/leads';
import {
  MyLeadsAttributes,
  MyLeadsCreateAudienceRequestParams,
  MyLeadsCreateAudienceResponse,
  MyLeadsSearchQuery,
  MyLeadsUrlsQueryType,
} from '../interfaces/myLeads';
import { ContactsModel } from '../model/ContactsModel';
import { activeFacebookPageIdSelector } from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class MyLeadsCRUDActions extends CrudActionContainer {
  retrieveLeadsBySearchAttributes: string;

  constructor() {
    super('MY_LEADS');
    this.retrieveLeadsBySearchAttributes = super.makeActionKey(
      'RETRIEVED_LEADS_BY_SEACH_ATTRIBUTES',
    );
  }

  retrieveAttributes({ skipBusy }: { skipBusy?: boolean }) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      if (!skipBusy) {
        dispatch(this.busy(this.retrieveAllAction));
      }
      try {
        const state = getState();
        const fbPageId = activeFacebookPageIdSelector(state);

        const response: MyLeadsAttributes =
          await MyLeadsController.getAllAttributes(fbPageId);

        dispatch(this.retrievedAll(response));
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  searchLeads(params: MyLeadsSearchQuery, skipBusy?: boolean) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      if (!skipBusy) {
        dispatch(this.busy(this.retrieveAllAction));
      }

      const state = getState();
      const fbPageId = activeFacebookPageIdSelector(state);
      try {
        const response: Partial<ContactsModel> =
          await MyLeadsController.searchLeads(fbPageId, params);
        dispatch({
          type: this.retrieveLeadsBySearchAttributes,
          payload: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  retrieveLocationAttributes(query: string) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const fbPageId = activeFacebookPageIdSelector(state);
      try {
        const response: SearchAttributes =
          await MyLeadsController.retrieveLocationAttributes(fbPageId, query);
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  createAudience(params: MyLeadsCreateAudienceRequestParams) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const fbPageId = activeFacebookPageIdSelector(state);
      try {
        const response: MyLeadsCreateAudienceResponse =
          await MyLeadsController.createAudience(fbPageId, params);
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  retrieveLandingPages(
    botId: number,
    query: string,
    type?: MyLeadsUrlsQueryType,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response: string[] = await MyLeadsController.retrieveLandingPages(
          botId,
          query,
          type,
        );
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  retrieveVisitedUrls(
    botId: number,
    query: string,
    type?: MyLeadsUrlsQueryType,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response: string[] = await MyLeadsController.retrieveVisitedUrls(
          botId,
          query,
          type,
        );
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }
}

const myLeadsCRUDActions = new MyLeadsCRUDActions();

export default myLeadsCRUDActions;

export const useMyLeadsCRUDActions = getActionHookBuilder(myLeadsCRUDActions, [
  'retrieveAttributes',
  'retrieveLocationAttributes',
  'retrieveLandingPages',
  'retrieveVisitedUrls',
  'searchLeads',
  'createAudience',
]);
